<template>
  <div class="bg-grey py-5" v-if="isParticipantsDisplayable && isLoggedIn">
    <div class="kws-int-container">
      <p class="error-box" v-if="isError" v-text="error"></p>
      <div v-if="isDisplayingActiveParticipantsAllowed">
        <h3 class="h3" v-if="translations.lblActiveParticipantsHeadline" v-text="translate('lblActiveParticipantsHeadline')"></h3>
        <div v-if="translations.lblActiveParticipantsSubHeadline" v-html="translate('lblActiveParticipantsSubHeadline')"></div>
        <div v-if="isActiveParticipantsEmpty">
          <h2 v-text="translate('lblNoActiveParticipantsFound')"></h2>
        </div>
        <ParticipantTable v-else :participants="activeParticipants" :currentUserMyId="currentUserMyId"></ParticipantTable>
      </div>
      <div v-if="isWaitingParticipantsActive">
        <h3 class="h3" v-if="translations.lblWaitingParticipantsHeadline" v-text="translate('lblWaitingParticipantsHeadline')"></h3>
        <div v-if="translations.lblWaitingParticipantsSubHeadline" v-html="translate('lblWaitingParticipantsSubHeadline')"></div>
        <ParticipantTable :participants="waitingParticipants" :currentUserMyId="currentUserMyId"></ParticipantTable>
      </div>
    </div>
    <LoadingIndicator v-if="isLoading"></LoadingIndicator>
  </div>
</template>

<script>
import EventMixin from './mixins/EventMixin';
import ParticipantTable from './components/participant-table.vue';
import LoadingIndicator from '../shared/components/loading-indicator.vue';

export default {
  name: 'EventParticipants',
  components: {
    LoadingIndicator,
    ParticipantTable,
  },
  data: () => ({
    activeParticipants: [],
    waitingParticipants: [],
    currentUserMyId: null,
    hasEventParam: false,
    error: null,
    loading: false,
  }),
  mixins: [EventMixin],
  props: {
    translations: Object,
    apiUrl: {
      type: String,
      required: true,
    },
    isParticipantsModuleActiveForThisProject: {
      type: Boolean,
      required: false,
      default: false,
    },
    isDisplayingActiveParticipantsAllowed: {
      type: Boolean,
      required: false,
      default: true,
    },
    isDisplayingWaitingParticipantsAllowed: {
      type: Boolean,
      required: false,
      default: true,
    },
    paging: Object,
    locale: String,
  },
  provide() {
    return {
      paging: this.paging,
      locale: this.locale,
    };
  },
  computed: {
    isError() {
      return this.error;
    },
    isLoading() {
      return this.loading && !this.isError;
    },
    isParticipantsDisplayable() {
      return this.isParticipantsModuleActiveForThisProject && this.hasEventParam;
    },
    isActiveParticipantsEmpty() {
      return !this.activeParticipants.length && !this.isError && !this.isLoading;
    },
    isWaitingParticipantsEmpty() {
      return !this.waitingParticipants.length && !this.isError && !this.isLoading;
    },
    isWaitingParticipantsActive() {
      return !this.isWaitingParticipantsEmpty && this.isDisplayingWaitingParticipantsAllowed;
    },
    isLoggedIn() {
      return !!window.KWS && !!window.KWS.user;
    },
  },
  methods: {
    isParticipantsDataInvalid(participantsData) {
      return !participantsData?.data?.info?.activeMembers?.length && participantsData?.data?.info?.waitingMembers.length;
    },
    getActiveParticipants(participantsData) {
      return participantsData?.data?.info?.activeMembers || [];
    },
    getWaitingParticipants(participantsData) {
      return participantsData?.data?.info?.waitingMembers || [];
    },
    renameParticipantKeys(unmappedParticipant) {
      const participantMapping = {
        Id: 'id',
        myUser_ID__c: 'myKwsUserId',
        firstName: 'firstName',
        lastName: 'lastName',
        street: 'street',
        zip: 'zip',
        city: 'city',
        mobile: 'mobile',
        phone: 'phone',
        EventRegistrationTime__c: 'registrationDate',
        EventRegistrationStatus__c: 'registrationStatus',
      };

      return Object.keys(unmappedParticipant)
        .reduce(
          (acc, key) => ({
            ...acc,
            ...{ [participantMapping[key] || key]: unmappedParticipant[key] },
          }),
          {},
        );
    },
    addWaitingPosition(participant, index) {
      participant.waitingPosition = index + 1;
      return participant;
    },
    numberConsecutively(participant, index) {
      participant.number = index + 1;
      return participant;
    },
    ascendingRegistrationDates(participantA, participantB) {
      return new Date(participantA.registrationDate) - new Date(participantB.registrationDate);
    },
    processParticipantsData(participantsData) {
      if (this.isParticipantsDataInvalid(participantsData)) { return; }

      this.activeParticipants = this.getActiveParticipants(participantsData)
        .map(this.renameParticipantKeys)
        .sort(this.ascendingRegistrationDates)
        .map(this.numberConsecutively);

      this.waitingParticipants = this.getWaitingParticipants(participantsData)
        .map(this.renameParticipantKeys)
        .sort(this.ascendingRegistrationDates)
        .map(this.addWaitingPosition);
    },
    processParticipatingData(participatingData) {
      this.currentUserMyId = participatingData?.data?.info?.myUser_ID__c ?? null;
    },
  },
  async created() {
    if (!this.isLoggedIn) return;
    if (!this.isParticipantsModuleActiveForThisProject) { return; }
    if (this.translations) {
      this.addDictionary(this.translations);
    }
    const urlParams = new URLSearchParams(window.location.search);
    if (!urlParams.has('event')) return;
    this.hasEventParam = true;
    this.processParticipatingData(await this.getEventData(`${this.apiUrl}/campaign/events/participating/`));
    this.processParticipantsData(await this.getEventData(`${this.apiUrl}/campaign/events/event/members/${urlParams.get('event')}/`));
  },
};
</script>
